// The family chart as a standalone SVG (colors inlined, no CSS needed), and as PNG pixels for
// image and PDF export. Draws the same layout and lines as the Tree view: around one person as
// the Tree view shows them, or the whole tree, with every family laid out side by side.

import { lsGet } from '../../model/db';
import { childrenOf, graphOf, other, parentsOf, partnersOf } from '../../model/graph';
import { displayName, initials, isDeceased } from '../../model/people';
import type { Id, Tree } from '../../model/types';
import { edgePaths, type EdgePath } from '../chart/edges';
import { cardHeight, cardLines, cardText, loadCardFields, nameWraps, textLeft, type CardField } from '../chart/cards';
import { CARD_W, layoutChart, ROW_GAP, UNIT_GAP, type Layout, type LayoutOptions } from '../chart/layout';
import { avatarColor } from '../components/common';
import { font, textWidth } from '../views/tableLayout';

const DEFAULT_OPTS: LayoutOptions = { ancestors: 3, descendants: 2, collateral: 'siblings', partners: true, maxPeople: 1500 };

/** The Tree view's current options (it saves them). */
export function viewOptions(): LayoutOptions {
  try {
    return { ...DEFAULT_OPTS, ...JSON.parse(lsGet('relationships.chartOptions') ?? '{}') };
  } catch {
    return DEFAULT_OPTS;
  }
}

/** Colors as references to the page's own CSS variables (see paint()). */
export const CSS_VAR_COLORS: Record<string, string> = new Proxy({}, { get: (_, k) => `var(${String(k)})` });

/** The most people a whole-tree chart draws. */
export const WHOLE_TREE_MAX = 5000;

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * Presentation attributes, except that colors given as CSS variables go in a style attribute,
 * since `fill="var(--x)"` isn't understood everywhere. The web page uses variables so its chart
 * follows the reader's light or dark mode.
 */
function paint(a: Record<string, string | number | undefined>): string {
  let out = '';
  let style = '';
  for (const [k, v] of Object.entries(a)) {
    if (v === undefined || v === '') continue;
    if (typeof v === 'string' && v.startsWith('var(')) style += `${k}:${v};`;
    else out += ` ${k}="${v}"`;
  }
  return out + (style ? ` style="${style}"` : '');
}

/** Line styles from styles.css (.edge…), with colors resolved. */
function edgeStyle(cls: string, c: Record<string, string>): Style {
  const has = (k: string) => cls.split(' ').includes(k);
  let stroke = c['--line'];
  let width = 1.6;
  let dash = '';
  let opacity = 1;
  let cap: 'round' | undefined;
  if (has('partner')) {
    stroke = c['--line-partner'];
    width = 2;
    if (has('ended')) {
      dash = '5 4';
      opacity = 0.7;
    }
  } else if (has('adoptive')) {
    stroke = c['--line-adoptive'];
    dash = '6 4';
  } else if (has('step')) {
    stroke = c['--line-step'];
    dash = '2 4';
  } else if (has('foster') || has('guardian')) {
    stroke = c['--line-foster'];
    dash = '6 4';
  } else if (has('chosen')) {
    stroke = c['--line-chosen'];
    dash = '1 4';
    width = 2.4;
    cap = 'round';
  }
  if (has('irregular')) opacity *= 0.75;
  return { fill: 'none', stroke, width, dash, opacity: opacity < 1 ? opacity : undefined, cap };
}

/** How a shape is filled and outlined. Colors are CSS colors (or var(--…) references on the web page). */
export interface Style {
  fill?: string;
  stroke?: string;
  width?: number;
  /** Dash and gap lengths, "5 4". */
  dash?: string;
  opacity?: number;
  cap?: 'round';
}

const styleAttrs = (st: Style) => paint({ fill: st.fill, stroke: st.stroke, 'stroke-width': st.width, 'stroke-dasharray': st.dash, opacity: st.opacity, 'stroke-linecap': st.cap });

/**
 * The picture as shapes, in its own pixels (y down), for drawing it as vectors in a PDF. Paths
 * are SVG path data (absolute M, L, H, V, C) offset by x and y.
 */
export type Shape =
  | { kind: 'rect'; x: number; y: number; w: number; h: number; r: number; style: Style }
  | { kind: 'path'; d: string; x: number; y: number; style: Style }
  | { kind: 'circle'; x: number; y: number; r: number; style: Style }
  /** A picture cropped to fill a circle (profile photos). */
  | { kind: 'photo'; href: string; x: number; y: number; size: number }
  /** `x` is the left edge, or the center when `middle`; `y` is the baseline; `width` is as measured on screen. */
  | { kind: 'text'; x: number; y: number; size: number; weight: number; text: string; color: string; middle: boolean; width: number; decorative?: boolean };

/** What drawing records besides the SVG: selectable text runs and the shapes. */
interface Rec {
  runs: TextRun[];
  shapes: Shape[];
}

type At = { x: number; y: number };

function rectEl(rec: Rec, at: At, x: number, y: number, w: number, h: number, r: number, st: Style, extra = ''): string {
  rec.shapes.push({ kind: 'rect', x: at.x + x, y: at.y + y, w, h, r, style: st });
  return `<rect${x ? ` x="${x}"` : ''}${y ? ` y="${y}"` : ''} width="${w}" height="${h}"${r ? ` rx="${r}"` : ''}${extra}${styleAttrs(st)}/>`;
}

function pathEl(rec: Rec, at: At, d: string, st: Style): string {
  rec.shapes.push({ kind: 'path', d, x: at.x, y: at.y, style: st });
  return `<path d="${d}"${styleAttrs(st)}/>`;
}

export interface ChartSvg {
  svg: string;
  width: number;
  height: number;
  people: number;
  /** Who's in it. */
  ids: Set<Id>;
  truncated: boolean;
  /** How many parts very wide families were split into (0 when none were). */
  parts?: number;
  /** Every label drawn, where it sits, for a PDF's selectable text layer. */
  texts: TextRun[];
  /** Everything drawn, for drawing the picture as vectors. */
  shapes: Shape[];
}

/** One line of text in the picture: its left edge and baseline in the picture's pixels, size, and measured width. */
export interface TextRun {
  x: number;
  y: number;
  size: number;
  width: number;
  text: string;
}

/**
 * A <text> element, also noted as a shape and (unless `select` is false, as for initials) as a
 * selectable run, at its place in the whole picture (`at` is its group's offset).
 */
function label(rec: Rec, at: At, x: number, y: number, size: number, weight: number, text: string, color: string, o: { middle?: boolean; extra?: string; select?: boolean } = {}): string {
  const width = textWidth(text, font(size, weight));
  const middle = !!o.middle;
  if (o.select !== false) rec.runs.push({ x: at.x + x - (middle ? width / 2 : 0), y: at.y + y, size, width, text });
  rec.shapes.push({ kind: 'text', x: at.x + x, y: at.y + y, size, weight, text, color, middle, width, ...(o.select === false ? { decorative: true } : {}) });
  return `<text x="${x}" y="${y}"${middle ? ' text-anchor="middle"' : ''} font-size="${size}"${weight !== 400 ? ` font-weight="${weight}"` : ''}${paint({ fill: color })}${o.extra ?? ''}>${esc(text)}</text>`;
}

export interface ChartHeading {
  title?: string;
  /** What the chart shows ("Around Ada Okafor", "Everyone in the tree"), shown under the title with the count and date. */
  around?: string;
  /** A key to the kinds of lines (and the deceased card color) the chart uses. */
  legend?: boolean;
  date?: Date;
}

export interface ChartStyle {
  /** CSS tokens (paletteTokens), as colors or as var(--…) references. */
  colors: Record<string, string>;
  /** What each card shows besides the name; by default, what the Tree view shows. */
  fields?: CardField[];
  heading?: ChartHeading;
  /** Each card is a link to the person's entry (#p-id) and can be linked to (#c-id), for the web page. */
  links?: boolean;
}

/** What each kind of line means, in the order the legend lists them. `cls` matches edgePaths()'s classes. */
const LEGEND: { test: (cls: string[]) => boolean; cls: string; label: string }[] = [
  { test: (c) => !c.includes('partner') && !['adoptive', 'step', 'foster', 'guardian', 'chosen'].some((k) => c.includes(k)), cls: 'edge', label: 'Parent and child' },
  { test: (c) => c.includes('partner') && !c.includes('ended'), cls: 'edge partner', label: 'Partners' },
  { test: (c) => c.includes('partner') && c.includes('ended'), cls: 'edge partner ended', label: 'Former partners' },
  { test: (c) => c.includes('adoptive'), cls: 'edge adoptive', label: 'Adoptive' },
  { test: (c) => c.includes('step'), cls: 'edge step', label: 'Step-parent' },
  { test: (c) => c.includes('foster') || c.includes('guardian'), cls: 'edge foster', label: 'Foster or guardian' },
  { test: (c) => c.includes('chosen'), cls: 'edge chosen', label: 'Chosen family' },
];

const LEGEND_ROW = 22;
const SAMPLE_W = 30;
const PAD = 32;
/** Space between families in a whole-tree chart. */
const BLOCK_GAP = 96;
const CAPTION_H = 28;

/** A piece of the chart: one family's layout, a part split off from one, or the people with no family links. */
interface Block {
  nodes: { id: Id; x: number; y: number }[];
  edges: EdgePath[];
  minX: number;
  minY: number;
  w: number;
  h: number;
  caption?: string;
  /** Where the caption starts, when not at the block's left edge: over the people a part hangs from. */
  captionX?: number;
  /** The caption's id, for links to the block ("part-2"). On the caption, not the whole block, so a link lands where the part joins. */
  anchor?: string;
  /** People drawn here again, at the top of a part, to show where it joins the rest. */
  repeats?: Set<Id>;
  /** Marker cards standing in for parts drawn elsewhere: what part, and how many people are in it. */
  markers?: Map<Id, { part: number; people: number }>;
}

function blockOf(nodes: Block['nodes'], edges: EdgePath[], cardH: number, more: Partial<Block> = {}): Block {
  const xs = nodes.map((n) => n.x);
  const ys = nodes.map((n) => n.y);
  const minX = Math.min(...xs) - CARD_W / 2;
  const minY = Math.min(...ys) - (more.caption ? CAPTION_H : 0);
  return { nodes, edges, minX, minY, w: Math.max(...xs) + CARD_W / 2 - minX, h: Math.max(...ys) + cardH - minY, ...more };
}

/** Card height for these people and fields: every card in a chart shares one. */
function cardHFor(tree: Tree, ids: Iterable<Id>, fields: CardField[]): number {
  const photo = fields.includes('photo');
  let two = false;
  for (const id of ids) if (nameWraps(displayName(tree.people[id]), photo)) { two = true; break; }
  return cardHeight(fields, two);
}

/** The chart around one person, as the layout options say. `tree` should already be redacted for export. */
export function chartSvg(tree: Tree, focus: Id, opts: LayoutOptions, style: ChartStyle): ChartSvg {
  const fields = style.fields ?? loadCardFields();
  const cardH = cardHFor(tree, Object.keys(tree.people), fields);
  const layout = layoutChart(tree, focus, { ...opts, cardH });
  const nodes = [...layout.nodes.values()];
  return draw(tree, [blockOf(nodes, edgePaths(layout), layout.cardH)], layout.cardH, layout.truncated, { ...style, fields });
}

/** Families: people linked by parents, partners, or siblings. Largest first; people with no such links last. */
export function families(tree: Tree): Id[][] {
  const parent = new Map<Id, Id>();
  const find = (x: Id): Id => {
    let r = x;
    while (parent.get(r) !== r) r = parent.get(r)!;
    parent.set(x, r);
    return r;
  };
  for (const id of Object.keys(tree.people)) parent.set(id, id);
  for (const r of Object.values(tree.relationships)) {
    if (r.kind === 'social' || !tree.people[r.a] || !tree.people[r.b]) continue;
    parent.set(find(r.a), find(r.b));
  }
  const groups = new Map<Id, Id[]>();
  for (const id of Object.keys(tree.people)) {
    const root = find(id);
    if (!groups.has(root)) groups.set(root, []);
    groups.get(root)!.push(id);
  }
  return [...groups.values()].sort((a, b) => b.length - a.length);
}

/** The least people worth splitting off as a part of their own. */
const MIN_PART = 6;
/** The most parts one family is split into. */
const MAX_PARTS = 80;

/** One family's piece: its own people, plus the people it hangs from, repeated from the part above. */
interface Part {
  members: Set<Id>;
  roots: Id[];
  /** Where the part attaches: its first generation (or, for the main part, its focus). They can't all leave. */
  anchors: Id[];
}

/** A marker card, in part `in`, that stands in for a part split off below the people in `parents`. */
interface Marker {
  id: Id;
  parents: Id[];
  part: Part;
  in: Part;
}

/** Just these people and the links among them (plus marker cards), for laying out one part. */
function subTree(tree: Tree, ids: Set<Id>, markers: Marker[]): Tree {
  const people: Tree['people'] = {};
  for (const id of ids) people[id] = tree.people[id];
  const relationships: Tree['relationships'] = {};
  for (const r of Object.values(tree.relationships)) if (r.kind !== 'social' && ids.has(r.a) && ids.has(r.b)) relationships[r.id] = r;
  for (const m of markers) {
    people[m.id] = { id: m.id, names: [{ given: '', surname: '', type: 'name' }], gender: { identity: 'unknown' }, living: null, events: [], notes: '', tags: [] };
    for (const p of m.parents) relationships[`${m.id}>${p}`] = { id: `${m.id}>${p}`, kind: 'parent', a: p, b: m.id, type: 'biological' };
  }
  return { ...tree, people, relationships, groups: {} };
}

/**
 * The families below one couple (or one parent) in a part, one per child (children whose
 * families are linked share one): each child and everyone linked to them without going back
 * through the couple. Ordered left to right as the current layout draws them.
 */
function familiesBelow(tree: Tree, part: Part, unit: Id[], layout: Layout): { members: Set<Id>; x: number }[] {
  const g = graphOf(tree);
  const inUnit = new Set(unit);
  const children = [...new Set(unit.flatMap((u) => childrenOf(g, u)))].filter((c) => part.members.has(c) && !inUnit.has(c));
  const taken = new Set<Id>();
  const out: { members: Set<Id>; x: number }[] = [];
  for (const child of children) {
    if (taken.has(child)) continue;
    const seen = new Set<Id>([child]);
    const queue = [child];
    for (let i = 0; i < queue.length; i++) {
      const x = queue[i];
      const next = [...parentsOf(g, x), ...childrenOf(g, x), ...partnersOf(g, x), ...(g.siblingRels.get(x) ?? []).map((r) => other(r, x))];
      for (const y of next) if (part.members.has(y) && !inUnit.has(y) && !seen.has(y)) { seen.add(y); queue.push(y); }
    }
    for (const id of seen) taken.add(id);
    const xs = children.filter((c) => seen.has(c)).map((c) => layout.nodes.get(c)?.x ?? 0);
    out.push({ members: seen, x: Math.min(...xs) });
  }
  return out.sort((p, q) => p.x - q.x);
}

/**
 * Like the periodic table's lanthanides, a family too wide for the page has a run of branches
 * lifted out (the last few children of a couple, with their families) and drawn as a part of
 * its own below, starting from the people it hangs from, with a marker card where it was.
 * Parts that are still too wide are split again.
 */
function splitFamily(tree: Tree, fam: Id[], focus: Id, cardH: number, maxW: number): { parts: Part[]; layouts: Layout[]; markers: Marker[] } {
  const g = graphOf(tree);
  const parts: Part[] = [{ members: new Set(fam), roots: [], anchors: [focus] }];
  const markers: Marker[] = [];
  const layouts: Layout[] = [];
  const lay = (p: Part) => layoutChart(subTree(tree, new Set([...p.members, ...p.roots]), markers.filter((m) => m.in === p)), p.roots[0] ?? focus, { ancestors: 0, descendants: 0, collateral: 'direct', partners: true, maxPeople: Infinity, whole: true, cardH });
  const widthOf = (l: Layout) => l.bounds.maxX - l.bounds.minX;
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    let layout = lay(part);
    while (widthOf(layout) > maxW && parts.length < MAX_PARTS) {
      // Move out about as many people as it takes to fit: the run of branches nearest that size, preferring one that isn't bigger.
      const want = part.members.size * (1 - maxW / widthOf(layout));
      const score = (n: number) => (n <= want ? want - n : (n - want) * 3);
      const units = new Map<string, Id[]>();
      for (const x of part.members) {
        const unit = [x, ...partnersOf(g, x).filter((p) => part.members.has(p))].sort();
        units.set(unit.join('|'), unit);
      }
      if (part.roots.length) units.set('roots', part.roots);
      let best: { members: Set<Id>; roots: Id[] } | undefined;
      for (const unit of units.values()) {
        const below = familiesBelow(tree, part, unit, layout);
        const run = new Set<Id>();
        // Runs from the right end, growing leftward; the leftmost branch always stays.
        for (let k = below.length - 1; k >= 1; k--) {
          for (const id of below[k].members) run.add(id);
          if (part.anchors.every((a) => run.has(a))) break;
          if (run.size < MIN_PART || part.members.size - run.size < MIN_PART) continue;
          if (!best || score(run.size) < score(best.members.size)) best = { members: new Set(run), roots: unit.filter((u) => childrenOf(g, u).some((c) => run.has(c))) };
        }
      }
      if (!best) break;
      const moved = best.members;
      for (const id of moved) part.members.delete(id);
      const branch: Part = { members: moved, roots: best.roots, anchors: best.roots.flatMap((r) => childrenOf(g, r)).filter((c) => moved.has(c)) };
      // Markers for parts that hang from people who just moved go with them.
      for (const m of markers) if (m.in === part && m.parents.every((x) => moved.has(x))) m.in = branch;
      markers.push({ id: `~part${markers.length}`, parents: best.roots, part: branch, in: part });
      parts.push(branch);
      layout = lay(part);
    }
    layouts.push(layout);
  }
  return { parts, layouts, markers };
}

/**
 * Everyone in the tree: each family laid out whole (with very wide ones split into parts, like
 * the periodic table's separate rows), packed in rows, then anyone with no family links.
 */
export function wholeTreeSvg(tree: Tree, style: ChartStyle): ChartSvg {
  const fields = style.fields ?? loadCardFields();
  const cardH = cardHFor(tree, Object.keys(tree.people), fields);
  const g = graphOf(tree);
  const linkCount = (id: Id) => (g.parentRels.get(id)?.length ?? 0) + (g.childRels.get(id)?.length ?? 0) + (g.partnerRels.get(id)?.length ?? 0);
  const people = Object.keys(tree.people).length;
  // About as wide as a square holding everyone, so the picture comes out page-shaped.
  const maxW = Math.max(3200, Math.sqrt(Math.min(people, WHOLE_TREE_MAX) * (CARD_W + UNIT_GAP) * (cardH + ROW_GAP)) * 1.9);
  const blocks: Block[] = [];
  const loners: Id[] = [];
  let left = WHOLE_TREE_MAX;
  let truncated = false;
  let partNo = 0;
  for (const fam of families(tree)) {
    if (left <= 0) {
      truncated = true;
      break;
    }
    if (fam.length === 1) {
      loners.push(fam[0]);
      left--;
      continue;
    }
    // Centered on the home person if they're in this family, else on its most connected person.
    const focus = fam.includes(tree.homePersonId ?? '') ? tree.homePersonId! : fam.reduce((best, id) => (linkCount(id) > linkCount(best) ? id : best), fam[0]);
    if (fam.length > left) {
      // The last family that fits only partly: as many as are left, unsplit.
      const layout = layoutChart(tree, focus, { ancestors: 0, descendants: 0, collateral: 'direct', partners: true, maxPeople: left, whole: true, cardH });
      truncated = true;
      left = 0;
      blocks.push(blockOf([...layout.nodes.values()], edgePaths(layout), cardH));
      continue;
    }
    left -= fam.length;
    const { parts, layouts, markers } = splitFamily(tree, fam, focus, cardH, maxW);
    const first = partNo + 1;
    parts.forEach((part, i) => {
      const layout = layouts[i];
      const no = first + i;
      const marks = new Map(markers.filter((m) => m.in === part).map((m) => [m.id, { part: first + parts.indexOf(m.part), people: m.part.members.size }]));
      const joins = markers.find((m) => m.part === part);
      const from = joins ? first + parts.indexOf(joins.in) : 0;
      const caption = parts.length === 1 ? undefined : from ? `Part ${no}, continued from part ${from}` : `Part ${no}`;
      const nodes = [...layout.nodes.values()];
      // A part's caption sits over the people it hangs from, where its marker link lands.
      const roots = nodes.filter((n) => part.roots.includes(n.id));
      const captionX = roots.length ? Math.min(...roots.map((n) => n.x)) - CARD_W / 2 : undefined;
      blocks.push(blockOf(nodes, edgePaths(layout), cardH, { caption, captionX, anchor: parts.length > 1 ? `part-${no}` : undefined, repeats: new Set(part.roots), markers: marks }));
    });
    if (parts.length > 1) partNo += parts.length;
  }
  if (loners.length) {
    const cols = Math.max(1, Math.min(loners.length, Math.ceil(Math.sqrt(loners.length * 2.5))));
    const nodes = loners
      .map((id) => tree.people[id])
      .sort((a, b) => displayName(a).localeCompare(displayName(b)))
      .map((p, i) => ({ id: p.id, x: (i % cols) * (CARD_W + 24), y: Math.floor(i / cols) * (cardH + 24) }));
    blocks.push(blockOf(nodes, [], cardH, { caption: blocks.length ? 'No family links recorded' : undefined }));
  }
  return { ...draw(tree, blocks, cardH, truncated, { ...style, fields }), parts: partNo };
}

/** Blocks placed left to right in rows about as wide as the widest family (or a squarish whole). */
function pack(blocks: Block[]): { at: { x: number; y: number }[]; w: number; h: number } {
  const area = blocks.reduce((s, b) => s + (b.w + BLOCK_GAP) * (b.h + BLOCK_GAP), 0);
  const rowW = Math.max(...blocks.map((b) => b.w), Math.min(6000, Math.sqrt(area) * 1.6));
  const at: { x: number; y: number }[] = [];
  let x = 0;
  let y = 0;
  let rowH = 0;
  let w = 0;
  for (const b of blocks) {
    if (x > 0 && x + b.w > rowW) {
      x = 0;
      y += rowH + BLOCK_GAP;
      rowH = 0;
    }
    at.push({ x, y });
    x += b.w + BLOCK_GAP;
    rowH = Math.max(rowH, b.h);
    w = Math.max(w, x - BLOCK_GAP);
  }
  return { at, w, h: y + rowH };
}

function draw(tree: Tree, blocks: Block[], cardH: number, truncated: boolean, style: ChartStyle & { fields: CardField[] }): ChartSvg {
  const { colors, fields, heading = {} } = style;
  const photo = fields.includes('photo');
  const textX = textLeft(photo);
  // Each person once: not their repeats at the top of a part, nor marker cards.
  const own = (b: Block) => b.nodes.filter((n) => !b.repeats?.has(n.id) && !b.markers?.has(n.id));
  const people = blocks.reduce((n, b) => n + own(b).length, 0);
  const { title } = heading;
  const subtitle = title
    ? [heading.around, `${people.toLocaleString('en-US')} ${people === 1 ? 'person' : 'people'}`, (heading.date ?? new Date()).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })].filter(Boolean).join(' · ')
    : '';
  const head = title ? 66 : 0;

  // The legend lists only what's drawn.
  const legend: { cls: string; label: string; swatch?: boolean; dashed?: boolean }[] = [];
  if (heading.legend) {
    const classes = blocks.flatMap((b) => b.edges.map((e) => e.cls.split(' ')));
    for (const l of LEGEND) if (classes.some(l.test)) legend.push(l);
    const dead = blocks.some((b) => own(b).some((n) => isDeceased(tree.people[n.id])));
    if (dead && colors['--card-dead-bg'] !== colors['--card-bg']) legend.push({ cls: '', label: 'Deceased', swatch: true });
    if (blocks.some((b) => b.repeats?.size)) legend.push({ cls: '', label: 'Shown again where a part joins', swatch: true, dashed: true });
  }
  const legendFont = font(12, 400);
  const itemW = legend.map((l) => SAMPLE_W + 8 + textWidth(l.label, legendFont) + 22);

  const packed = pack(blocks);
  const titleW = title ? Math.max(textWidth(title, font(22, 650)), textWidth(subtitle, font(13, 400))) : 0;
  const width = Math.ceil(Math.max(packed.w, titleW, Math.max(0, ...itemW)) + PAD * 2);
  // Legend items flow into rows that fit the width.
  const rows: number[][] = [];
  itemW.forEach((w, i) => {
    const row = rows[rows.length - 1];
    if (row && row.reduce((s, j) => s + itemW[j], 0) + w <= width - PAD * 2) row.push(i);
    else rows.push([i]);
  });
  const foot = rows.length ? 20 + rows.length * LEGEND_ROW : 0;
  const height = Math.ceil(packed.h + PAD * 2 + head + foot);
  const left = (width - packed.w) / 2;
  const fontAttr = `font-family="ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif"`;
  const parts: string[] = [];
  const rec: Rec = { runs: [], shapes: [] };
  const top = { x: 0, y: 0 };
  parts.push(`<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" ${fontAttr}>`);
  if (title) parts.push(`<title>${esc(title)}</title>`);
  parts.push(`<defs><clipPath id="ac" clipPathUnits="objectBoundingBox"><circle cx="0.5" cy="0.5" r="0.5"/></clipPath></defs>`);
  parts.push(rectEl(rec, top, 0, 0, width, height, 0, { fill: colors['--bg'] }));
  if (title) {
    parts.push(label(rec, top, PAD, PAD + 12, 22, 650, title, colors['--text']));
    parts.push(label(rec, top, PAD, PAD + 34, 13, 400, subtitle, colors['--text-muted']));
  }
  blocks.forEach((b, bi) => {
    const ox = left + packed.at[bi].x - b.minX;
    const oy = PAD + head + packed.at[bi].y - b.minY;
    parts.push(`<g transform="translate(${ox},${oy})">`);
    const at = { x: ox, y: oy };
    if (b.caption) parts.push(label(rec, at, b.captionX ?? b.minX, b.minY + 14, 13, 600, b.caption, colors['--text-muted'], { extra: b.anchor ? ` id="${b.anchor}"` : '' }));
    for (const e of b.edges) parts.push(pathEl(rec, at, e.d, edgeStyle(e.cls, colors)));
    for (const n of b.nodes) {
      const marker = b.markers?.get(n.id);
      const cardAt = { x: ox + n.x - CARD_W / 2, y: oy + n.y };
      parts.push(marker ? markerCard(n, cardH, marker, style, rec, cardAt) : card(tree, n, cardH, fields, photo, textX, style, rec, cardAt, b.repeats?.has(n.id)));
    }
    parts.push(`</g>`);
  });
  if (rows.length) {
    let y = height - PAD - rows.length * LEGEND_ROW + 14;
    parts.push(`<g>`);
    for (const row of rows) {
      let x = PAD;
      for (const i of row) {
        const l = legend[i];
        if (l.swatch) parts.push(rectEl(rec, top, x + 4, y - 11, SAMPLE_W - 8, 14, 3, l.dashed ? { fill: colors['--card-bg'], stroke: colors['--text-muted'], dash: '3 3' } : { fill: colors['--card-dead-bg'], stroke: colors['--border'] }));
        else parts.push(pathEl(rec, top, `M${x},${y - 4} H${x + SAMPLE_W}`, edgeStyle(l.cls, colors)));
        parts.push(label(rec, top, x + SAMPLE_W + 8, y, 12, 400, l.label, colors['--text-muted']));
        x += itemW[i];
      }
      y += LEGEND_ROW;
    }
    parts.push(`</g>`);
  }
  parts.push(`</svg>`);
  return { svg: parts.join(''), width, height, people, ids: new Set(blocks.flatMap((b) => own(b).map((n) => n.id))), truncated, texts: rec.runs, shapes: rec.shapes };
}

/** A card standing in for a part drawn elsewhere: "Continued in part 3 · 120 people". */
function markerCard(n: { x: number; y: number }, cardH: number, m: { part: number; people: number }, style: ChartStyle, rec: Rec, at: At): string {
  const { colors } = style;
  const move = `transform="translate(${n.x - CARD_W / 2},${n.y})"`;
  const mid = cardH / 2;
  const body = [
    rectEl(rec, at, 0, 0, CARD_W, cardH, 10, { fill: colors['--bg'], stroke: colors['--text-muted'], dash: '4 4' }),
    label(rec, at, CARD_W / 2, mid - 3, 13.5, 650, `Continued in part ${m.part}`, colors['--text'], { middle: true }),
    label(rec, at, CARD_W / 2, mid + 14, 12, 400, `${m.people.toLocaleString('en-US')} ${m.people === 1 ? 'person' : 'people'}`, colors['--text-muted'], { middle: true }),
  ].join('');
  return style.links ? `<a href="#part-${m.part}" ${move}><title>Go to part ${m.part}</title>${body}</a>` : `<g ${move}>${body}</g>`;
}

/** One person's card. A repeat (where a part joins the rest) is dashed and isn't a link target, since the person's own card is elsewhere. */
function card(tree: Tree, n: { id: Id; x: number; y: number }, cardH: number, fields: CardField[], photo: boolean, textX: number, style: ChartStyle, rec: Rec, at: At, repeat = false): string {
  const { colors } = style;
  const p = tree.people[n.id];
  const text = cardText(displayName(p), cardLines(tree, p, fields), photo, cardH);
  const avatarY = Math.min(cardH / 2, text.nameY[0] + 12);
  const parts: string[] = [];
  const move = `transform="translate(${n.x - CARD_W / 2},${n.y})"`;
  parts.push(style.links ? `<a href="#p-${esc(p.id)}"${repeat ? '' : ` id="c-${esc(p.id)}"`} ${move}>` : `<g ${move}>`);
  if (style.links) parts.push(`<title>${esc(displayName(p))}</title>`);
  const fill = isDeceased(p) ? colors['--card-dead-bg'] : colors['--card-bg'];
  parts.push(rectEl(rec, at, 0, 0, CARD_W, cardH, 10, repeat ? { fill, stroke: colors['--text-muted'], dash: '4 4' } : { fill, stroke: colors['--border'] }, ' class="card"'));
  if (!photo) {
    /* no picture or initials */
  } else if (p.photo) {
    rec.shapes.push({ kind: 'photo', href: p.photo, x: at.x + 8, y: at.y + avatarY - 16, size: 32 });
    parts.push(`<image href="${esc(p.photo)}" x="8" y="${avatarY - 16}" width="32" height="32" clip-path="url(#ac)" preserveAspectRatio="xMidYMid slice"/>`);
  } else {
    rec.shapes.push({ kind: 'circle', x: at.x + 24, y: at.y + avatarY, r: 16, style: { fill: avatarColor(p.id) } });
    parts.push(`<circle cx="24" cy="${avatarY}" r="16" fill="${avatarColor(p.id)}"/>`);
    parts.push(label(rec, at, 24, avatarY + 4, 12, 700, initials(p), '#fff', { middle: true, select: false }));
  }
  text.name.forEach((line, i) => parts.push(label(rec, at, textX, text.nameY[i], 13.5, 650, line, colors['--text'])));
  text.details.forEach((line, i) => {
    const size = i === 0 ? 12 : 11;
    parts.push(label(rec, at, textX, text.detailY[i], size, 400, line, i === 0 ? colors['--text-muted'] : colors['--text-faint']));
  });
  parts.push(style.links ? `</a>` : `</g>`);
  return parts.join('');
}

/** The same picture showing only `crop` (in its own pixels), for printing a big chart in pieces. */
export function cropSvg(chart: ChartSvg, crop: { x: number; y: number; w: number; h: number }): ChartSvg {
  const svg = chart.svg.replace(/^<svg ([^>]*?)width="[^"]*" height="[^"]*" viewBox="[^"]*"/, `<svg $1width="${crop.w}" height="${crop.h}" viewBox="${crop.x} ${crop.y} ${crop.w} ${crop.h}"`);
  return { ...chart, svg, width: crop.w, height: crop.h };
}

/** Browsers cap canvas size; stay inside the common limits. */
const MAX_SIDE = 16000;
const MAX_AREA = 120_000_000;
const PRINT_AREA = 40_000_000;

export function safeScale(w: number, h: number, wanted: number): number {
  return Math.min(wanted, MAX_SIDE / w, MAX_SIDE / h, Math.sqrt(MAX_AREA / (w * h)));
}

/** Pixels per chart pixel that give about 300 dots per inch at `ptPerPx` points per chart pixel, within the canvas limits. */
export function printScale(chart: { width: number; height: number }, ptPerPx: number): number {
  // Each page's pixels are read back into memory to compress, so keep a page under about 40 megapixels.
  const wanted = Math.min(4, Math.max(2, (300 / 72) * ptPerPx), Math.sqrt(PRINT_AREA / (chart.width * chart.height)));
  return safeScale(chart.width, chart.height, wanted);
}

export async function rasterize(chart: ChartSvg, scale: number, background: string): Promise<HTMLCanvasElement> {
  const s = safeScale(chart.width, chart.height, scale);
  const url = URL.createObjectURL(new Blob([chart.svg], { type: 'image/svg+xml' }));
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(chart.width * s);
    canvas.height = Math.round(chart.height * s);
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function canvasBlob(canvas: HTMLCanvasElement, type: 'image/png' | 'image/jpeg', quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('The image was too large to create.'))), type, quality));
}
